import { Button } from '@components/atoms/Button/Button';
import { Input } from '@components/atoms/Input/Input';
import { Provider } from '@components/atoms/Provider/Provider';
import { Title } from '@components/atoms/Title';
import { Channel } from '@components/molecules/Channel/Channel';
import { FavoriteItem } from '@components/molecules/FavoriteItem/FavoriteItem';
import { Default } from '@components/templates/layouts/Default';
import { favoriteRequestContext } from '@lib/graphql/favorites';
import { extractSsrState } from '@lib/graphql/helpers/extractSsrState';
import { publicGraphqlContext } from '@lib/graphql/publicRequest';
import { createUrqlClient, useAuthQuery, useMutation, usePaginatedQuery } from '@lib/graphql/urql';
import {
  type Cinema,
  CreateTvmFavoriteMultipleDocument,
  type Genre as GenreType,
  GetChannelsDocument,
  GetCinemasDocument,
  type Provider as ProviderType,
  RemoveTvmFavoriteMultipleDocument,
  type TvChannel,
  TvmFavoritesSimpleDocument,
  TvmFavoriteType,
} from '@src/lib/graphql/generated';
import { useLoginStore } from '@src/stores/login';
import { providers } from '@utils/providers';
import { LoadingIndicator } from '@vgn-medien-holding/vgn-fe-components';
import { useRouter } from 'next/router';
import { getToken } from 'next-auth/jwt';
import { useSession } from 'next-auth/react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

type FavoriteType = 'sender' | 'provider' | 'kinos';
type Change = { id: string; action: 'add' | 'remove'; type: TvmFavoriteType };

export type FavoriteEntityContentType = {
  title: string;
  loginFlowTitle: string;
  dataObject: string;
  type: TvmFavoriteType;
  fetchType: 'query' | 'local';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  query?: any;
  localArray?: unknown[];
  variables?: object;
  Component: (entry: TvChannel | GenreType | ProviderType | Cinema) => React.JSX.Element;
};

export type FavoriteEntityType = Record<FavoriteType, FavoriteEntityContentType>;

const entityTypes: FavoriteEntityType = {
  sender: {
    title: 'Sender',
    loginFlowTitle: 'Speichere jetzt Deine Lieblingssender',
    dataObject: 'channels',
    type: TvmFavoriteType.Channel,
    fetchType: 'query',
    query: GetChannelsDocument,
    variables: {
      first: 30,
      page: 1,
    },
    Component: (entry: TvChannel) => (
      <Channel channel={entry} onlyLogo classProps={{ root: 'p-3 max-w-40 mx-auto', image: 'py-4' }} />
    ),
  },
  provider: {
    title: 'Provider',
    loginFlowTitle: 'Speichere jetzt Deine Lieblings-Streaming-Dienste',
    dataObject: 'provider',
    type: TvmFavoriteType.Provider,
    fetchType: 'local',
    localArray: providers,
    Component: (entry: ProviderType) => (
      <Provider
        provider={entry}
        style="onlyImage"
        classProps={{
          onlyImage: 'grid items-center content-center justify-center p-3 w-full min-h-0 min-w-0 mx-auto aspect-auto',
          image: 'max-h-16 max-w-24 line-clamp-3',
        }}
      />
    ),
  },
  kinos: {
    title: 'Lieblingskinos',
    loginFlowTitle: 'Speichere jetzt Deine Lieblingskinos',
    dataObject: 'cinemas',
    type: TvmFavoriteType.Cinema,
    fetchType: 'query',
    query: GetCinemasDocument,
    variables: { first: 200, page: 1 },
    Component: (entry: Cinema) => (
      <div className="px-3 py-4">
        <div className="line-clamp-2 font-bold leading-body">{entry.name}</div>
        {entry.city?.name && <div className="mt-1 text-gray-400 text-sm">{entry.city.name}</div>}
      </div>
    ),
  },
};

function Favorites({ data }) {
  return <FavoritesSelection key={data.type} entityType={data.type} />;
}

function FavoritesSelection({ entityType }: { entityType: FavoriteType }) {
  const { updateLoginStore } = useLoginStore(
    useShallow((state) => ({
      updateLoginStore: state.updateLoginStore,
    })),
  );
  const entity: FavoriteEntityContentType = entityTypes[entityType];
  const router = useRouter();
  const { status } = useSession() ?? {};
  const [lsChanges, setLsChanges] = useLocalStorage<Change[]>('favChanges', [], {
    serializer: (value) => JSON.stringify(value),
    deserializer: (value) => JSON.parse(value),
  });

  const [isLoading, setIsLoading] = useState(false);
  const [changes, setChanges] = useState<Change[]>(() => lsChanges.filter((change) => change.type === entity.type));
  const [search, setSearch] = useState('');

  // ! TODO: hasMorePages returns false all the time - API
  const [{ fetching, error: entitiesError, data: entitiesData, fetchNextPage, hasMorePages }] = usePaginatedQuery({
    query: entity?.query ?? GetChannelsDocument,
    context: publicGraphqlContext,
    variables: entity?.variables,
    pause: entity?.fetchType !== 'query' || (entityType === 'kinos' && status !== 'authenticated'),
  });

  const [{ data: favoritesData, fetching: favoritesLoading, error: favoritesError }] = useAuthQuery({
    query: TvmFavoritesSimpleDocument,
    context: favoriteRequestContext,
    requestPolicy: 'network-only',
    variables: {
      includeTypes: [entity.type],
      first: 999,
      page: 1,
    },
    pause: status !== 'authenticated',
  });

  const [favorites, setFavorites] = useState<string[]>([]);

  useEffect(() => {
    const userFavorites = favoritesData?.tvmFavorites?.data;
    setFavorites(userFavorites?.map((e) => e.entity?.id).filter(Boolean) ?? []);
  }, [favoritesData?.tvmFavorites?.data]);

  const [, executeMutationCreateFavorites] = useMutation(CreateTvmFavoriteMultipleDocument);
  const [, executeMutationRemoveFavorites] = useMutation(RemoveTvmFavoriteMultipleDocument);

  const entries =
    (entity.fetchType === 'local'
      ? entity.localArray
      : entitiesData?.flatMap((data) => data?.[entity.dataObject]?.data)
    )?.filter(Boolean) ?? [];
  const visibleEntries =
    entityType === 'kinos'
      ? (entries as Cinema[])
          .filter((entry) =>
            `${entry.name ?? ''} ${entry.city?.name ?? ''}`
              .toLocaleLowerCase('de-AT')
              .includes(search.trim().toLocaleLowerCase('de-AT')),
          )
          .sort(
            (a, b) =>
              (a.city?.name ?? '').localeCompare(b.city?.name ?? '', 'de-AT', { sensitivity: 'base' }) ||
              (a.name ?? '').localeCompare(b.name ?? '', 'de-AT', { sensitivity: 'base', numeric: true }),
          )
      : entries;
  const entryGroups =
    entityType === 'kinos'
      ? Object.entries(
          (visibleEntries as Cinema[]).reduce<Record<string, Cinema[]>>((groups, cinema) => {
            const state = (cinema.city?.state_code === 'at-0' ? '' : cinema.city?.state?.trim()) || '';
            groups[state] ??= [];
            groups[state].push(cinema);
            return groups;
          }, {}),
        )
          .sort(([a], [b]) => {
            // Keep cinemas without a federal state after the named groups.
            if (!a) return 1;
            if (!b) return -1;
            return a.localeCompare(b, 'de-AT', { sensitivity: 'base' });
          })
          .map(([state, entries]) => ({ title: state || 'Weitere Kinos', entries }))
      : [{ title: '', entries: visibleEntries }];

  async function saveChanges() {
    const toast = (await import('react-toastify')).toast;
    if (status !== 'authenticated') {
      setLsChanges((previous) => [...previous.filter((change) => change.type !== entity.type), ...changes]);
      updateLoginStore({ loginFlowOpen: true, loginFlowTitle: entity.loginFlowTitle });
      return;
    }
    if (isLoading || favoritesLoading || favoritesError) return;
    if (changes.length < 1) {
      toast.info('Keine Änderungen registriert.', { autoClose: 1500, delay: 0 });
      return;
    }
    setIsLoading(true);
    try {
      const additionIds = changes.filter((e) => e.action === 'add').map((e) => ({ entity: e.id, type: e.type }));
      const results = { addition: null, removal: null };
      if (additionIds.length > 0) {
        results.addition = await executeMutationCreateFavorites(
          {
            data: additionIds,
          },
          favoriteRequestContext,
        );
      }

      const removalIds = changes.filter((e) => e.action === 'remove').map((e) => e.id);
      if (removalIds.length > 0) {
        results.removal = await executeMutationRemoveFavorites(
          {
            entityIds: removalIds,
          },
          favoriteRequestContext,
        );
      }

      if (results.addition?.error || results.removal?.error) {
        toast.error('Das Speichern deiner Favoriten hat leider nicht geklappt.');
      } else {
        setLsChanges((previous) => previous.filter((change) => change.type !== entity.type));
        setFavorites((prev) => [
          ...(prev?.length > 0 ? prev.filter((e) => !removalIds.includes(e)) : []),
          ...additionIds.map((e) => e.entity),
        ]);
        setChanges([]);
        toast.success('Deine Favoriten wurden gespeichert.');
      }
    } catch {
      toast.error('Das Speichern deiner Favoriten hat leider nicht geklappt.');
    } finally {
      setIsLoading(false);
    }
  }

  function handleFavoriteAction(id: string, isFavorite: boolean) {
    setChanges((prev) => {
      const existingChange = prev.find((e) => e.id === id);
      if (existingChange) {
        if (existingChange.action === (isFavorite ? 'add' : 'remove')) {
          return prev;
        } else {
          return prev.filter((e) => e.id !== id);
        }
      } else {
        return [...prev, { id, action: isFavorite ? 'add' : 'remove', type: entity.type }];
      }
    });
  }

  function getFavoriteStatus(entry) {
    if (!entry) return false;
    const change = changes.find((e) => e.id === entry.id);
    if (change) {
      return change.action === 'add';
    }
    return favorites?.includes(entry.id);
  }

  return (
    <section>
      <Title level={5}>{entity.title}</Title>
      {entityType === 'kinos' && status === 'unauthenticated' ? (
        <div className="mt-8">
          <p>Melde dich an, um Deine Lieblingskinos auszuwählen.</p>
          <Button classProps={{ root: 'mt-6' }} link="/auth/login?callbackUrl=%2Ffavoriten%2Fkinos">
            Anmelden
          </Button>
        </div>
      ) : status === 'loading' || favoritesLoading || (fetching && entries.length === 0) ? (
        <LoadingIndicator visible />
      ) : entitiesError || favoritesError ? (
        <p role="alert" className="mt-8">
          Die Auswahl konnte nicht geladen werden. Bitte lade die Seite erneut.
        </p>
      ) : (
        <>
          {entityType === 'kinos' && (
            <Input
              id="favorite-cinema-search"
              label="Kino oder Ort suchen"
              classProps={{ root: 'mt-6 max-w-md' }}
              inputProps={{ type: 'search', value: search, onChange: (event) => setSearch(event.target.value) }}
            />
          )}
          <div className="mt-8 space-y-8">
            {entryGroups.map((group) => (
              <div key={group.title}>
                {group.title && (
                  <Title level={6} classProps={{ heading: 'mb-4' }}>
                    {group.title}
                  </Title>
                )}
                <div className="grid grid-cols-fill-streaming-squares gap-4">
                  {group.entries.map((entry) => (
                    <FavoriteItem
                      isFavorite={getFavoriteStatus(entry)}
                      onAction={(isFavorite) => handleFavoriteAction(entry.id, isFavorite)}
                      key={entry.id}
                      disabled={isLoading}
                    >
                      {entity.Component(entry)}
                    </FavoriteItem>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {visibleEntries.length === 0 && <p className="mt-6">Keine Einträge gefunden.</p>}
          <div className="contents flex-wrap gap-4 md:flex">
            {hasMorePages && (
              <Button
                classProps={{ root: 'mt-6' }}
                buttonStyle="secondary"
                hasAction
                onAction={() => fetchNextPage()}
                isLoading={fetching}
                disabled={fetching || isLoading}
              >
                Mehr laden
              </Button>
            )}
            <div className="sticky inset-x-0 bottom-0 z-10 flex flex-1 gap-4 bg-black p-4 sm:justify-end md:static md:inset-x-auto md:bottom-auto md:mt-6 md:bg-transparent md:p-0">
              <Button
                classProps={{ root: 'w-full sm:w-auto' }}
                buttonStyle="secondary"
                hasAction
                onAction={() => router.back()}
              >
                Abbrechen
              </Button>
              <Button
                classProps={{ root: 'w-full sm:w-auto' }}
                buttonStyle={changes.length > 0 ? 'primary' : 'secondary'}
                hasAction
                onAction={saveChanges}
                isLoading={isLoading}
                disabled={isLoading}
              >
                Speichern
              </Button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

export async function getServerSideProps({ params, res, req }) {
  res.setHeader('Cache-Control', 'private, no-store');

  const { ssrAuthQuery, ssrCache } = createUrqlClient();

  const entitySlug = params.slug;
  const entity = entityTypes[params.slug];

  if (!entity) return { notFound: true };

  if (entitySlug === 'kinos' && !(await getToken({ req }))) {
    return { redirect: { destination: '/auth/login?callbackUrl=%2Ffavoriten%2Fkinos', permanent: false } };
  }

  await ssrAuthQuery({
    query: TvmFavoritesSimpleDocument,
    variables: { includeTypes: [entity.type], first: 999, page: 1 },
    req,
  });

  return {
    props: {
      data: {
        type: entitySlug ?? null,
      },
      urqlState: extractSsrState(ssrCache),
    },
  };
}

Favorites.PageLayout = Default;

export default Favorites;
